import { Station, StationNameFormat, useGetStations } from "@/entities/station";
import { createStationsRankFunction } from "@/features/infra/components/stations-filter-scoring.ts";
import { getStationOptions } from "@/features/infra/components/utils.ts";
import { RankFunction } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { MultiSelectFormField } from "@/shared/components/ui/multi-select-form-field.tsx";
import { useCallback, useMemo } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

type StationMultiSelectProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    name: FieldPath<T>;
    label?: string | null;
    placeholder?: string;
    badgeLabel?: "value" | StationNameFormat;
    helpText?: string;
    nlStationsOnly?: boolean;
    triggerFormValidationOnChange?: boolean;
    "data-testid"?: string;
    filterFunction?: (station: Station) => boolean; // Custom filter that overrides nlStationsOnly
};

export function StationMultiSelect<T extends FieldValues>({
    form,
    name,
    label = "Selecteer stations",
    placeholder = "meerdere...",
    badgeLabel = "stationCodeSuffixed",
    nlStationsOnly = false,
    triggerFormValidationOnChange = false,
    filterFunction,
    helpText,
    "data-testid": dataTestid,
}: Readonly<StationMultiSelectProps<T>>) {
    const { data: stations, isPending, error } = useGetStations({ select: nlStationsOnly ? Station.nlStationsOnly : undefined });
    const getLabel = useCallback(
        (station: Station) => {
            if (badgeLabel === "value") {
                return station.code;
            } else {
                return Station.getDisplayName(station, badgeLabel);
            }
        },
        [badgeLabel]
    );

    const includedStations = useMemo(() => {
        if (filterFunction) {
            return stations?.filter(filterFunction) ?? [];
        }

        if (nlStationsOnly) {
            return stations?.filter((s) => s.landCode === "NL") ?? [];
        }

        return stations ?? [];
    }, [stations, nlStationsOnly, filterFunction]);

    const options = useMemo(() => getStationOptions(includedStations, getLabel), [getLabel, includedStations]);

    const rankFn: RankFunction | undefined = useMemo(() => {
        return createStationsRankFunction([], includedStations);
    }, [includedStations]);

    if (isPending) {
        return <p>Laden...</p>;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van stations" />;
    }

    return (
        <MultiSelectFormField
            form={form}
            name={name}
            options={options}
            label={label === null ? undefined : label}
            search={{ emptyMessage: "Geen stations gevonden", placeholder: "Zoek station..." }}
            placeholder={placeholder}
            helpText={helpText}
            data-testid={dataTestid}
            rankFn={rankFn}
            triggerFormValidationOnChange={triggerFormValidationOnChange}
        />
    );
}
